import { CircleInfoIcon } from "@xola/icons";
import { Alert, flash, Label, SubmitButton } from "@xola/ui-kit";
import { getSellerExperiencesFromPartnerPackage, Loader, requiredNumberAtleastZero, SecondaryButton } from "common";
import { useFormikContext } from "formik";
import { isEmpty, omit, uniqBy } from "lodash";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
    Experience,
    FeatureRole,
    LineItemCode,
    Package,
    PartnerPackageWithExpandedProducts,
    PaymentMethod,
    PurchaseRule,
    PurchaseRuleLink,
    PurchaseRuleLinkEntityObject,
} from "types";
import { number, object } from "yup";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useCollection } from "../../../hooks/useCollection";
import { getExistingPurchaseRule, getPurchaseRulesFromPurchaseRuleLinks } from "../../../models/PurchaseRuleLink";
import { RadioInput } from "../../Common/RadioInput";
import { XolaFormik } from "../../Common/XolaFormik";
import { ContentLayout } from "../../Layout/ContentLayout";
import { BasicTerm, PackagePricingValues } from "../Packages/EditPackageBasicDiscount";
import { PackageExperienceAddOnDiscountList } from "../Packages/PackageExperienceAddOnDiscountList";
import {
    getPricingTermDiscount,
    updateBasicTermPurchaseRule,
    updatePurchaseRulePayload,
} from "../Packages/PackagePricingHelper";
import {
    DiscountPricingValue,
    getPackagePurchaseRuleLinkPayload,
    getTemplateId,
    TermsType,
    useLineItemTemplateByCode,
} from "../Packages/PricingSelectorHelper";
import { ExperienceDemographicList } from "./ExperienceDemographicList";
import { useInvalidate } from "@/hooks/useInvalidation";
import { hasFeature } from "@/models/Seller";

const validationSchema = object().shape({
    basicDiscount: object().shape({
        discount: number().when("termType", {
            is: TermsType.BASIC,
            then: number().positive().max(100).min(0).required("is required"),
        }),
    }),
    downDeposit: object()
        .default(null)
        .nullable()
        .shape({
            amount: requiredNumberAtleastZero(
                "should be a positive integer",
                "should be a positive integer",
                "is required",
            ),
            days: requiredNumberAtleastZero(
                "should be a positive integer",
                "should be a positive integer",
                " is required",
            ).integer("should be a positive integer"),
        }),
});

export const EditPartnerPackagePricingPage = ({ pkg }: { pkg: PartnerPackageWithExpandedProducts }) => {
    const { data: purchaseRuleLinks, isLoading: isLoadingPurchaseRuleLink } = useCollection<PurchaseRuleLink>(
        "purchaseRuleLinks",
        {
            "entities.id": pkg.id,
            "entities.object": PurchaseRuleLinkEntityObject.PARTNER_PACKAGE,
            expand: "purchaseRule",
        },
    );

    if (isLoadingPurchaseRuleLink) {
        return <Loader />;
    }

    return <EditPartnerPackagePricing pkg={pkg} purchaseRuleLinks={purchaseRuleLinks} />;
};

export const EditPartnerPackagePricing = ({
    pkg,
    purchaseRuleLinks,
}: {
    pkg: PartnerPackageWithExpandedProducts;
    purchaseRuleLinks: PurchaseRuleLink[];
}) => {
    const history = useHistory();
    const seller = useSeller();
    const invalidate = useInvalidate();
    const hasRealtimeUpdateEnabled = hasFeature(seller, FeatureRole.REALTIME_UPDATES);
    const { data: discountTemplate } = useLineItemTemplateByCode(LineItemCode.PARTNER_PACKAGE_DISCOUNT);

    const purchaseRules = getPurchaseRulesFromPurchaseRuleLinks(purchaseRuleLinks);
    const [purchaseRuleState, setPurchaseRuleState] = useState(purchaseRules);
    const [basicDiscount, setBasicDiscount] = useState(getPricingTermDiscount(purchaseRuleState, purchaseRuleLinks));
    const [isAdvanceSelected, setIsAdvanceSelected] = useState(basicDiscount.termType === TermsType.ADVANCED);

    const initialValues: PackagePricingValues = {
        paymentMethod: PaymentMethod.CREDIT_CARD,
        downDeposit: pkg.downDeposit,
        basicDiscount: {
            discount: basicDiscount.discount ?? 0,
            termType: basicDiscount.termType,
            includeAddons: basicDiscount.includeAddons,
        },
    };

    const selectedExperiences = getSellerExperiencesFromPartnerPackage(pkg, seller.id);

    useEffect(() => {
        const purchaseRules = getPurchaseRulesFromPurchaseRuleLinks(purchaseRuleLinks);
        if (purchaseRules.length > 0) {
            setPurchaseRuleState(purchaseRules);
            if (!isAdvanceSelected) {
                setBasicDiscount(getPricingTermDiscount(purchaseRules, purchaseRuleLinks));
            }
        }
    }, [purchaseRuleLinks, isAdvanceSelected]);

    const handleDemographicChange = (discountPricingValues: DiscountPricingValue[], experience: Experience) => {
        const purchaseRule = getPurchaseRuleForExperience(purchaseRuleState, experience.id);
        if (purchaseRule) {
            updatePurchaseRule(purchaseRule, discountPricingValues, experience.id, purchaseRule.meta?.addons);
        }
    };

    const handleAddOnChange = (
        discountPricingValues: DiscountPricingValue[],
        experience: Experience,
        isEnabled?: boolean,
    ) => {
        const purchaseRule = getPurchaseRuleForExperience(purchaseRuleState, experience.id);
        if (purchaseRule && !isEmpty(discountPricingValues)) {
            updatePurchaseRule(purchaseRule, discountPricingValues, experience.id, isEnabled);
        } else if (purchaseRule) {
            // update addon enabled state
            setPurchaseRuleState((previousPurchaseRuleState) =>
                previousPurchaseRuleState.map((purchaseRuleItr) =>
                    purchaseRuleItr.id !== purchaseRule.id || purchaseRuleItr.meta?.subProductId !== experience.id
                        ? purchaseRuleItr
                        : { ...purchaseRuleItr, meta: { ...purchaseRuleItr.meta, addons: isEnabled } },
                ),
            );
        }
    };

    const updatePurchaseRule = (
        purchaseRule: PurchaseRule,
        discountPricingValues: DiscountPricingValue[],
        productId: ID,
        isEnabled = true,
    ) => {
        let purchaseRuleStates: PurchaseRule[] = [];

        const templateIds = new Set(discountPricingValues.map(({ action }) => getTemplateId(action)));

        // Old actions either demographics or addons that already exist and currently not updating
        const filteredActions = purchaseRule.actions.filter((actionItr) => {
            const actionTemplateId = getTemplateId(actionItr) ?? "";
            return !templateIds.has(actionTemplateId);
        });

        // New actions either demographics or addons that we currently updating
        const updatedActions = discountPricingValues
            .filter(({ action }) => (!action.id && !action._remove) || action.id)
            .map((value) => value.action);

        // already saved actions with id
        const oldUniqActions = uniqBy(
            [...filteredActions.filter((action) => action.id), ...updatedActions.filter((action) => action.id)],
            "id",
        );

        // new actions without ids
        const newActions = [
            ...filteredActions.filter((action) => !action.id),
            ...updatedActions.filter((action) => !action.id),
        ];

        // Other purchase rules except this current experience rules
        const purchaseRules = purchaseRuleState.filter(({ id }) => {
            return purchaseRule.id !== id || purchaseRule.meta?.subProductId !== productId;
        });

        purchaseRuleStates = [
            ...purchaseRules,
            {
                ...purchaseRule,
                meta: {
                    ...purchaseRule.meta,
                    subProductId: productId,
                    addons: isEnabled,
                },
                actions: [...oldUniqActions, ...newActions],
            },
        ];

        setPurchaseRuleState(purchaseRuleStates);
    };

    const handleCancel = () => {
        history.push("/products/partnerPackages");
    };

    const handleSubmit = async (values: PackagePricingValues) => {
        if (values.basicDiscount.termType === TermsType.BASIC) {
            const updatedPurchaseRules = updateBasicTermPurchaseRule(
                values,
                selectedExperiences,
                purchaseRuleState,
                discountTemplate,
            );
            setPurchaseRuleState(updatedPurchaseRules);
            handlePurchaseRules(values, updatedPurchaseRules, purchaseRules);
        } else {
            handlePurchaseRules(values, purchaseRuleState, purchaseRules);
        }

        // Updating package in the end as this will invalidate the purchaseRuleLinks in pusher.ts
        await updatePackage(omit(values, "basicDiscount"));

        if (!hasRealtimeUpdateEnabled) {
            invalidate("partnerPackages", pkg.id);
        }

        flash.show({ text: "Successfully updated package", color: "success" });
    };

    const handlePurchaseRules = async (
        values: PackagePricingValues,
        purchaseRules?: PurchaseRule[],
        originalPurchaseRules?: PurchaseRule[],
    ) => {
        const purchaseRulePromises: Array<Promise<PurchaseRule>> = [];

        for (const purchaseRule of purchaseRules ?? purchaseRuleState) {
            const originalPurchaseRule = originalPurchaseRules?.find((rule) => rule.id === purchaseRule.id);
            purchaseRulePromises.push(
                savePurchaseRule(updatePurchaseRulePayload(selectedExperiences, purchaseRule, originalPurchaseRule)),
            );
        }

        await Promise.all(purchaseRulePromises).then(async (purchaseRules) => {
            purchaseRules.map(async (purchaseRule) => {
                const purchaseRuleLink = getExistingPurchaseRule(purchaseRuleLinks, purchaseRule);
                const updatedPurchaseLink = await savePurchaseRuleLink(
                    getPackagePurchaseRuleLinkPayload(
                        seller,
                        pkg,
                        purchaseRule,
                        purchaseRuleLink,
                        values.basicDiscount,
                    ),
                );

                invalidate("purchaseRuleLinks", updatedPurchaseLink.id);
            });
        });
    };

    return (
        <ContentLayout isWide>
            <XolaFormik
                enableReinitialize
                initialValues={{ ...initialValues, ...pkg, isFormDirty: false }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting, handleSubmit }) => {
                    return (
                        <>
                            <h2 className="mt-5">Pricing</h2>
                            <div className="divide-y divide-gray-light">
                                <TermType
                                    isAdvanceSelected={isAdvanceSelected}
                                    setIsAdvanceSelected={setIsAdvanceSelected}
                                />

                                <ContentLayout.Section
                                    title="Terms"
                                    description="Choose if the discount applies to add-ons or not"
                                >
                                    <Alert color="warning" className="mt-2 mb-2 py-4 px-6">
                                        <div className="flex items-center space-x-2">
                                            <CircleInfoIcon size="large" className="align-middle" />
                                            <p>
                                                With Partner Packages, the terms you configure here apply only to your
                                                products. Operator terms will apply to their products.
                                            </p>
                                        </div>
                                    </Alert>

                                    {isAdvanceSelected ? (
                                        selectedExperiences.map((experience: Experience) => (
                                            <div key={experience.id}>
                                                <ExperienceDemographicList
                                                    experience={experience}
                                                    purchaseRule={getPurchaseRuleForExperience(
                                                        purchaseRuleState,
                                                        experience.id,
                                                    )}
                                                    partnerPackage={pkg}
                                                    onChangeDemographicDiscount={(
                                                        discountPricingValues: DiscountPricingValue[],
                                                    ) => handleDemographicChange(discountPricingValues, experience)}
                                                />

                                                <PackageExperienceAddOnDiscountList
                                                    experience={experience}
                                                    purchaseRule={getPurchaseRuleForExperience(
                                                        purchaseRuleState,
                                                        experience.id,
                                                    )}
                                                    onChangeAddOnDiscount={(
                                                        discountPricingValue: DiscountPricingValue[],
                                                        isEnabled,
                                                    ) => handleAddOnChange(discountPricingValue, experience, isEnabled)}
                                                />
                                            </div>
                                        ))
                                    ) : (
                                        <BasicTerm />
                                    )}
                                </ContentLayout.Section>

                                <ContentLayout.Footer preferenceSidebar>
                                    <SubmitButton
                                        isLoading={isSubmitting}
                                        disabled={isSubmitting}
                                        onClick={handleSubmit}
                                    >
                                        Save
                                    </SubmitButton>
                                    <SecondaryButton type="reset" onClick={handleCancel}>
                                        Cancel
                                    </SecondaryButton>
                                </ContentLayout.Footer>
                            </div>
                        </>
                    );
                }}
            </XolaFormik>
        </ContentLayout>
    );
};

const updatePackage = async (payload: Partial<Package>) => {
    return api.put(`/partnerPackages/${payload.id}`, payload);
};

const savePurchaseRule = async (purchaseRule: PurchaseRule) => {
    const { data: savedPurchaseRule } = await api.put<PurchaseRule>(`/purchaseRules/${purchaseRule.id}`, purchaseRule);

    return savedPurchaseRule;
};

const savePurchaseRuleLink = async (purchaseRuleLink: PurchaseRuleLink) => {
    if (purchaseRuleLink.id) {
        const { data: savedPurchaseRuleLink } = await api.put<PurchaseRuleLink>(
            `/purchaseRuleLinks/${purchaseRuleLink.id}`,
            purchaseRuleLink,
        );
        return savedPurchaseRuleLink;
    }

    const { data: savedPurchaseRuleLink } = await api.post<PurchaseRuleLink>(`/purchaseRuleLinks`, purchaseRuleLink);

    return savedPurchaseRuleLink;
};

const getPurchaseRuleForExperience = (purchaseRules: PurchaseRule[], productId: ID) => {
    return purchaseRules.find(
        (purchaseRule) => purchaseRule.meta?.product === productId || purchaseRule.meta?.subProductId === productId,
    );
};

export const TermType = ({
    isAdvanceSelected,
    setIsAdvanceSelected,
}: {
    isAdvanceSelected: boolean;
    setIsAdvanceSelected: (isAdvanceSelected: boolean) => void;
}) => {
    const { setFieldValue } = useFormikContext<PackagePricingValues>();

    return (
        <ContentLayout.Section
            title="Configuration"
            description="Select terms to configure a custom price for your package’s Demographics and Add-ons."
        >
            <Label>Terms</Label>
            <div className="flex space-x-6">
                <RadioInput
                    checked={!isAdvanceSelected}
                    onChange={() => {
                        setFieldValue("basicDiscount.termType", TermsType.BASIC);
                        setIsAdvanceSelected(false);
                    }}
                >
                    Basic
                </RadioInput>
                <RadioInput
                    checked={isAdvanceSelected}
                    onChange={() => {
                        setFieldValue("basicDiscount.termType", TermsType.ADVANCED);
                        setIsAdvanceSelected(true);
                    }}
                >
                    Advanced
                </RadioInput>
            </div>
        </ContentLayout.Section>
    );
};
